import { useMutation } from "@apollo/client";
import { gql } from "../../__generated__";
import { amplitude } from "../third-party";
import {
  AiAnimatedElementsGeneration,
  AiAnimatedElementsGenerationStatus,
  AiRevealAspectRatio,
  AiRevealImagePosition,
  RevealsModelName,
  TaskStatus,
} from "../../__generated__/graphql";
import { MY_GENERATIONS } from "./generationList";
import { downloadFromUrl } from "../firebaseStorage";
import { isUUID } from "../../common/utils/uuid";
import { useMemo, useState } from "react";
import { useMeasure } from "@archery-inc/design-system";

export const GENERATION_FRAGMENT = gql(`
  fragment GenerationFragment on AiAnimatedElementsGeneration {
    _id
    progress
    generationId
    inputFileUrl
    inputBucketInfo {
      bucketName
      filePath
    }
    status
    feedback {
      rating
      message
    }
    presets {
      aspectRatio
      imagePosition
      margin
      customPrompt
      coordinates {
        x
        y
      }
      backgroundColor
      presetId
      modelName
      audioTask {
        status
        jsonPayload
      }
      videoDuration
      task {
        status
        parsedResult
        jsonPayload
        intermediateResults {
          url
        }
        thumbnailData {
          thumbHash
          thumbnailUrl
        }
        metadata
      }
    }
  }
`);

export const GENERATION_COMPLETE_FRAGMENT = gql(`
  fragment GenerationCompleteFragment on AiAnimatedElementsGeneration {
    _id
    progress
    generationId
    inputFileUrl
    inputBucketInfo {
      bucketName
      filePath
    }
    status
    feedback {
      rating
      message
    }
    presets {
      aspectRatio
      imagePosition
      margin
      customPrompt
      coordinates {
        x
        y
      }
      videoDuration
      backgroundColor
      presetId
      modelName
      audioTask {
        status
        jsonPayload
        parsedResult
        outputVideo {
          url
          hasWatermark
        }
      }
      task {
        status
        parsedResult
        jsonPayload
        intermediateResults {
          url
        }
        outputVideo {
          url
          hasWatermark
        }
        thumbnailData {
          thumbHash
          thumbnailUrl
        }
      }
    }
  }
`);

const CANCEL_GENERATION = gql(`
  mutation CancelGeneration($generationId: ID!) {
    cancelGeneration(generationId: $generationId) {
      generationId
      status
    }
  }
`);

export function useCancelGeneration(generationId: string) {
  return useMutation(CANCEL_GENERATION, {
    variables: { generationId },
    optimisticResponse: {
      __typename: "Mutation",
      cancelGeneration: {
        generationId,
        status: AiAnimatedElementsGenerationStatus.Canceled,
      },
    },
    update(cache, { data }) {
      const prevdata = cache.readQuery({ query: MY_GENERATIONS });
      if (data?.cancelGeneration && prevdata) {
        cache.writeQuery({
          query: MY_GENERATIONS,
          data: {
            ...prevdata,
            myAiGenerationsAnonymous: {
              ...prevdata.myAiGenerationsAnonymous,
              data: prevdata.myAiGenerationsAnonymous.data.map((g) =>
                (g as AiAnimatedElementsGeneration).generationId ===
                generationId
                  ? {
                      ...g,
                      status: data.cancelGeneration.status,
                    }
                  : g,
              ),
            },
          },
        });
      }
    },
  });
}

const CREATE_GENERATION = gql(`
  mutation CreateAiAnimatedElementsWithPresets(
    $inputFileUrl: String!
    $presets: [PresetConfig!]
    $inputBucketInfo: BucketInfoInput
  ) {
    createAiAnimatedElementsWithPresetsAnonymous(
      inputFileUrl: $inputFileUrl
      presets: $presets
      inputBucketInfo: $inputBucketInfo
    ) {
      ...GenerationFragment
    }
  }
`);

export function useCreateGeneration() {
  return useMutation(CREATE_GENERATION, {
    update(cache, { data }) {
      const generation =
        data?.createAiAnimatedElementsWithPresetsAnonymous as AiAnimatedElementsGeneration;
      const prevdata = cache.readQuery({ query: MY_GENERATIONS });
      if (generation && prevdata) {
        cache.writeQuery({
          query: MY_GENERATIONS,
          data: {
            myAiGenerationsAnonymous: {
              ...prevdata.myAiGenerationsAnonymous,
              data: [generation, ...prevdata.myAiGenerationsAnonymous.data],
            },
          },
        });
      }
    },
  });
}

const DELETE_GENERATION = gql(`
  mutation DeleteAiGeneration($generationId: ID!) {
    deleteAiGeneration(generationId: $generationId) {
      generationId
    }
  }
`);

export function useDeleteGeneration({
  generationId,
}: {
  generationId: string;
}) {
  return useMutation(DELETE_GENERATION, {
    variables: { generationId },
    optimisticResponse: {
      __typename: "Mutation",
      deleteAiGeneration: {
        generationId,
      },
    },
    update(client, { data }) {
      const generationId = data?.deleteAiGeneration.generationId;
      if (generationId) {
        // Remove generation from query
        const prevdata = client.readQuery({ query: MY_GENERATIONS });
        client.writeQuery({
          query: MY_GENERATIONS,
          overwrite: true,
          data: {
            ...prevdata,
            myAiGenerationsAnonymous: {
              ...(prevdata?.myAiGenerationsAnonymous ?? {}),
              data: prevdata
                ? prevdata.myAiGenerationsAnonymous.data.filter(
                    (g) =>
                      (g as AiAnimatedElementsGeneration).generationId !==
                      generationId,
                  )
                : [],
            },
          },
        });
      }
    },
  });
}

export const isGenerationProcessing = (
  generation: AiAnimatedElementsGeneration,
) => {
  const status = getGenerationStatus(generation);
  return [
    GenerationStatus.Preparing,
    GenerationStatus.Queued,
    GenerationStatus.Generating,
  ].includes(status);
};

export const shouldRefetchGeneration = (
  generation: AiAnimatedElementsGeneration,
) => {
  const status = getGenerationStatus(generation);
  return [
    GenerationStatus.Preparing,
    GenerationStatus.Queued,
    GenerationStatus.Generating,
    GenerationStatus.AudioQueued,
    GenerationStatus.AudioProcessing,
  ].includes(status);
};

export enum GenerationStatus {
  Preparing = "preparing",
  Queued = "queued",
  Complete = "complete",
  Generating = "processing",
  AudioQueued = "audioQueued",
  AudioProcessing = "audioProcessing",
  Canceled = "canceled",
  Error = "error",
}

export function getGenerationStatus(
  generation: AiAnimatedElementsGeneration,
): GenerationStatus {
  const preset = generation?.presets?.[0];
  if (
    [
      AiAnimatedElementsGenerationStatus.Error,
      AiAnimatedElementsGenerationStatus.TasksCompletedWithErrors,
      AiAnimatedElementsGenerationStatus.TasksPartialError,
    ].includes(generation.status)
  ) {
    return GenerationStatus.Error;
  }
  if (generation.status === AiAnimatedElementsGenerationStatus.Canceled) {
    return GenerationStatus.Canceled;
  }
  if (preset?.task?.status === TaskStatus.Completed) {
    if (preset?.audioTask?.status === TaskStatus.Queued) {
      return GenerationStatus.AudioQueued;
    }
    if (preset?.audioTask?.status === TaskStatus.Processing) {
      return GenerationStatus.AudioProcessing;
    }
    return GenerationStatus.Complete;
  }
  if (!preset?.task || preset?.task?.status === TaskStatus.Preparing) {
    return GenerationStatus.Preparing;
  }
  if (preset.task?.status === TaskStatus.Queued) {
    return GenerationStatus.Queued;
  }
  return GenerationStatus.Generating;
}

export function getAspectRatioString(ratio: AiRevealAspectRatio) {
  switch (ratio) {
    case AiRevealAspectRatio.RunwayLandscapeV4:
    case AiRevealAspectRatio.Landscape:
      return "16 / 9";
    case AiRevealAspectRatio.RunwayPortraitV4:
    case AiRevealAspectRatio.Portrait:
      return "9 / 16";
    case AiRevealAspectRatio.Vertical:
      return "4 / 5";
    case AiRevealAspectRatio.Square:
      return "1 / 1";
    case AiRevealAspectRatio.RunwayLandscape:
      return "5 / 3";
    case AiRevealAspectRatio.RunwayPortrait:
      return "3 / 5";
    default:
      return "9 / 16";
  }
}

export function downloadGeneration(generation: AiAnimatedElementsGeneration) {
  const preset = generation.presets[0];
  if (!preset) return;

  // parsedResult is a JSON scalar (typed `any` by codegen); for an audio task
  // it holds the rendered video URL.
  const videoUrl =
    (preset.audioTask?.parsedResult as string | undefined) ??
    preset.task?.outputVideo?.url;

  amplitude.logEvent("Web:Client:MojoAI:Download", {
    model:
      preset.modelName === RevealsModelName.Runway ? "runway" : "controlnet",
    generationId: generation.generationId,
    presetId: isUUID(preset.presetId) ? "custom" : preset.presetId,
    dbPresetId: preset.presetId,
  });
  window.top?.postMessage({ type: "VIDEO_DOWNLOAD_STARTED" }, "*");
  if (!videoUrl) return;
  void downloadFromUrl(videoUrl, `${preset.presetId}-${new Date().getTime()}`);
}

export function useImagePosition(
  imgPosition: AiRevealImagePosition,
  margin = 0.1,
  currentX?: number,
  currentY?: number,
) {
  const [imgAspectRatio, setImgAspectRatio] = useState(0);
  const [ref, { width, height }] = useMeasure();

  const { x, y, imgW, imgH, minMargin } = useMemo(() => {
    if (!width || !height) {
      return { x: 0, y: 0, imgW: 0, imgH: 0, minMargin: 0 };
    }
    let w = Math.floor((1 - 2 * margin) * width);
    let h = Math.ceil(w / imgAspectRatio);
    let minMarg = 0.5 - (height * imgAspectRatio) / (2 * width);
    if (width > height * imgAspectRatio) {
      h = Math.floor((1 - 2 * margin) * height);
      w = Math.ceil(h * imgAspectRatio);
      minMarg = 0.5 - width / (2 * height * imgAspectRatio);
    }

    const p = 0;
    let left: number;
    let top: number;
    switch (imgPosition) {
      case AiRevealImagePosition.Center:
        [left, top] = [(width - w) / 2, (height - h) / 2];
        break;
      case AiRevealImagePosition.Left:
        [left, top] = [p, (height - h) / 2];
        break;
      case AiRevealImagePosition.Right:
        [left, top] = [width - w - p, (height - h) / 2];
        break;
      case AiRevealImagePosition.Top:
        [left, top] = [(width - w) / 2, p];
        break;
      case AiRevealImagePosition.Bottom:
        [left, top] = [(width - w) / 2, height - h - p];
        break;
      case AiRevealImagePosition.TopLeft:
        [left, top] = [p, p];
        break;
      case AiRevealImagePosition.TopRight:
        [left, top] = [width - w - p, p];
        break;
      case AiRevealImagePosition.BottomLeft:
        [left, top] = [p, height - h - p];
        break;
      case AiRevealImagePosition.BottomRight:
        [left, top] = [width - w - p, height - h - p];
        break;
      case AiRevealImagePosition.Custom:
        [left, top] = [currentX! * width, currentY! * height];
        break;
      default:
        [left, top] = [(width - w) / 2, (height - h) / 2];
        break;
    }
    return {
      x: left / width,
      y: top / height,
      imgW: w,
      imgH: h,
      minMargin: minMarg,
    };
  }, [currentX, currentY, height, imgAspectRatio, imgPosition, margin, width]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImgAspectRatio(
      e.currentTarget.naturalWidth / e.currentTarget.naturalHeight,
    );
  };

  return { ref, imgW, imgH, x, y, onImageLoad, width, height, minMargin };
}
